/**
 * Rest interval detection. Two paths:
 *
 * 1. **Primary — lap-gap-derived.** When lap events exist, gaps > 25s between
 *    consecutive lap-end and the next lap-start are rests. This is the most
 *    reliable signal: more accurate than HR valleys (which lag and round) and
 *    more accurate than segment events (which sometimes overlap actively
 *    swimming time).
 *
 * 2. **Fallback — HR valley.** When the workout has no lap events, fall back
 *    to detecting stretches where HR drops below `thresholdRatio × avgHr` for
 *    `minDurationSeconds`. Used for legacy workouts without auto-lap.
 */
import type { Lap } from "./laps";
import type { HrPoint } from "./hrSeries";
import { REST_GAP_MS } from "./restConstant";

export interface RestInterval {
  startMs: number;
  endMs: number;
  durationSeconds: number;
  minHr: number;
}

/** Build rest intervals from inter-lap gaps; falls back to HR-valley detection. */
export function buildRestIntervals(
  laps: Lap[],
  hrSeries: HrPoint[],
  avgHr: number | null,
): RestInterval[] {
  const out: RestInterval[] = [];
  // D-05 SPECIAL CASE: iterate ALL laps (do NOT filter on effectiveExcluded).
  // The gap between an excluded wall-lap and its neighbor STILL defines a rest
  // interval — excluding the lap from aggregate rollups must never erase the
  // gap measurement. This function produces no per-lap aggregate, only gaps, so
  // there is nothing to filter; the rest survives by construction.
  for (let i = 1; i < laps.length; i++) {
    const prev = laps[i - 1];
    const cur = laps[i];
    const gapMs = cur.startMs - prev.endMs;
    if (gapMs < REST_GAP_MS) continue;
    const gapHrs = hrSeries.filter((p) => p.ts >= prev.endMs && p.ts <= cur.startMs);
    const minHr = gapHrs.length > 0
      ? Math.min(...gapHrs.map((p) => p.bpm))
      : (avgHr ?? 0);
    out.push({
      startMs: prev.endMs,
      endMs: cur.startMs,
      durationSeconds: gapMs / 1000,
      minHr,
    });
  }
  if (out.length === 0 && avgHr !== null) {
    return detectRestIntervals(hrSeries, avgHr);
  }
  return out;
}

/**
 * HR-valley fallback. Find stretches where HR drops below threshold for at
 * least `minDurationSeconds`. Threshold = `thresholdRatio × avgHr`
 * (default 75% of workout avg). Returns sorted, non-overlapping intervals.
 */
export function detectRestIntervals(
  hrSeries: HrPoint[],
  avgHr: number,
  thresholdRatio = 0.75,
  minDurationSeconds = 20,
): RestInterval[] {
  if (hrSeries.length === 0 || avgHr <= 0) return [];
  const threshold = avgHr * thresholdRatio;
  const out: RestInterval[] = [];
  let cursor: { startMs: number; endMs: number; minHr: number } | null = null;
  for (const p of hrSeries) {
    if (p.bpm < threshold) {
      if (cursor === null) {
        cursor = { startMs: p.ts, endMs: p.ts, minHr: p.bpm };
      } else {
        cursor.endMs = p.ts;
        if (p.bpm < cursor.minHr) cursor.minHr = p.bpm;
      }
    } else if (cursor !== null) {
      const durSec = (cursor.endMs - cursor.startMs) / 1000;
      if (durSec >= minDurationSeconds) {
        out.push({ startMs: cursor.startMs, endMs: cursor.endMs, durationSeconds: durSec, minHr: cursor.minHr });
      }
      cursor = null;
    }
  }
  if (cursor !== null) {
    const durSec = (cursor.endMs - cursor.startMs) / 1000;
    if (durSec >= minDurationSeconds) {
      out.push({ startMs: cursor.startMs, endMs: cursor.endMs, durationSeconds: durSec, minHr: cursor.minHr });
    }
  }
  return out;
}
